// v2/pages/CartV2.js — sepet sayfasi
import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ShoppingCart, ArrowLeft, Undo2, X, Trash2, AlertTriangle,
  Package, Minus, Plus, CheckSquare, ChevronLeft, ChevronRight,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import CartTable from '../components/CartTable';
import CartSummary from '../components/CartSummary';
import SiparisGonderimi from '../components/SiparisGonderimi';
import ses from '../utils/ses';
import ToastStack from '../components/Toast';
import useToast from '../hooks/useToast';
import useMenuNav from '../hooks/useMenuNav';
import useCartContext from '../hooks/CartContext';
import { WAREHOUSES } from '../constants/catalog';
import useExchangeRates from '../hooks/useExchangeRates';
import { formatTRY, formatNumber } from '../utils/format';
import { withVat, VAT_RATE } from '../utils/discount';
import styles from '../styles/DashboardV2.module.css';
import page from '../styles/CartV2.module.css';
import '../styles/tokens.css';


/** Mobil kart gorunumu */
function MobileCard({ line, onQty, onRemove, lineUnits }) {
  const p = line.product;
  const units = lineUnits(line);
  const locked = line.status === 'transferred';

  return (
    <div className={`${page.mCard} ${locked ? page.mCardLocked : ''}`}>
      <div className={page.mTop}>
        <span className={page.mThumb}>
          {p.image
            ? <img className={page.mThumbImg} src={p.image} alt="" />
            : <Package size={22} strokeWidth={1.3} />}
        </span>
        <div className={page.mInfo}>
          <span className={page.mCode}>{p.productCode}</span>
          <span className={page.mName}>{p.description}</span>
        </div>
        {!locked && (
          <button type="button" className={page.mDel}
                  onClick={() => onRemove(line.key)} aria-label="Kaldır">
            <Trash2 size={15} />
          </button>
        )}
      </div>

      <div className={page.mChips}>
        {p.discounts.map((d, i) => (
          <span key={i} className={`${page.mChip} ${d.kind === 'general' ? page.mChipCamp : ''}`}>
            %{formatNumber(d.rate, 2)}
          </span>
        ))}
      </div>

      <div className={page.mWh}>
        {WAREHOUSES.map((w) => {
          const q = line.qty[w.key] || 0;
          const stock = p[w.key] || 0;
          return (
            <div key={w.key} className={page.mWhBox}>
              <span className={page.mWhName}>{w.city}</span>
              <span className={page.mWhStock}>{stock > 0 ? 'Mevcut' : 'Stok Yok'}</span>
              {locked ? (
                <span className={page.mQtyLocked}>{q}</span>
              ) : (
                <span className={page.mQty}>
                  <button type="button" className={page.mQtyBtn} disabled={q <= 0}
                          onClick={() => onQty(line.key, w.key, Math.max(q - 1, 0))}
                          aria-label={`${w.city} azalt`}>
                    <Minus size={13} />
                  </button>
                  <span className={page.mQtyVal}>{q}</span>
                  <button type="button" className={page.mQtyBtn}
                          onClick={() => onQty(line.key, w.key, q + 1)}
                          aria-label={`${w.city} artır`}>
                    <Plus size={13} />
                  </button>
                </span>
              )}
            </div>
          );
        })}
      </div>

      <div className={page.mFoot}>
        <span className={page.mUnits}>{units} adet</span>
        <span className={page.mPrices}>
          <span className={page.mGross}>{formatTRY(p.grossPrice * units)}</span>
          <span className={page.mNet}>{formatTRY(p.netPrice * units)}</span>
        </span>
      </div>
      <div className={page.mSave}>
        Kazancınız {formatTRY(p.savingAmount * units)}
      </div>
    </div>
  );
}

export default function CartV2() {
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();
  const [note, setNote] = useState('');
  const [confirmClear, setConfirmClear] = useState(false);
  const [gonderiliyor, setGonderiliyor] = useState(false);
  // ref kilidi: state guncellenmeden gelen ikinci tiklama ikinci istegi baslatamaz
  const gonderKilit = useRef(false);
  const [sonSiparis, setSonSiparis] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const otoSecilen = useRef(new Set());
  const [tab, setTab] = useState('pending'); // 'pending' | 'all'
  const confirmRef = useRef(null);
  const cart = useCartContext();
  const toast = useToast();
  const kurlar = useExchangeRates();
  const EUR_RATE = kurlar.eurRate;

  const {
    lines, pending, totals, stockIssues,
    setQty, remove, removeMany, clear, siparisVer,
    lastRemoved, undoRemove, dismissUndo, lineUnits,
  } = cart;

  const siparisliler = useMemo(
    () => lines.filter((l) => l.status !== 'pending'),
    [lines]
  );
  const shown = tab === 'pending' ? pending
    : tab === 'ordered' ? siparisliler
    : lines;
  const empty = lines.length === 0;

  // --- Sayfalama (istemci tarafli) ---
  // Toplamlar TUM sepetten hesaplanir; burada sadece gorunen satirlar bolunur.
  const SAYFA_BOYU = 10;
  const [sayfa, setSayfa] = useState(1);
  const toplamSayfa = Math.max(Math.ceil(shown.length / SAYFA_BOYU), 1);
  const aktifSayfa = Math.min(sayfa, toplamSayfa);

  // Sekme degisince veya satir silinince sayfa disina dusmeyelim
  useEffect(() => { setSayfa(1); }, [tab]);
  useEffect(() => {
    setSayfa((s) => Math.min(s, Math.max(Math.ceil(shown.length / SAYFA_BOYU), 1)));
  }, [shown.length]);

  const gorunen = useMemo(
    () => shown.slice((aktifSayfa - 1) * SAYFA_BOYU, aktifSayfa * SAYFA_BOYU),
    [shown, aktifSayfa]
  );

  const sayfaListesi = useMemo(() => {
    if (toplamSayfa <= 7) return Array.from({ length: toplamSayfa }, (_, i) => i + 1);
    if (aktifSayfa <= 4) return [1, 2, 3, 4, 5, '…', toplamSayfa];
    if (aktifSayfa >= toplamSayfa - 3) {
      return [1, '…', toplamSayfa - 4, toplamSayfa - 3, toplamSayfa - 2, toplamSayfa - 1, toplamSayfa];
    }
    return [1, '…', aktifSayfa - 1, aktifSayfa, aktifSayfa + 1, '…', toplamSayfa];
  }, [aktifSayfa, toplamSayfa]);

  // silinen satirlarin secimi temizlensin + yeni bekleyen satirlar otomatik secili gelsin
  useEffect(() => {
    const mevcut = new Set(lines.map((l) => l.key));
    otoSecilen.current = new Set([...otoSecilen.current].filter((k) => mevcut.has(k)));

    setSelected((cur) => {
      const next = new Set([...cur].filter((k) => mevcut.has(k)));
      let degisti = next.size !== cur.size;
      lines.forEach((l) => {
        if (l.status !== 'pending') return;
        if (otoSecilen.current.has(l.key)) return;   // daha once otomatik secildi
        otoSecilen.current.add(l.key);
        if (!next.has(l.key)) { next.add(l.key); degisti = true; }
      });
      return degisti ? next : cur;
    });
  }, [lines]);

  const toggleSelect = useCallback((key) => {
    setSelected((cur) => {
      const next = new Set(cur);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    setSelected((cur) => {
      const selectable = shown.filter((l) => l.status !== 'transferred');
      const allOn = selectable.length > 0 && selectable.every((l) => cur.has(l.key));
      return allOn ? new Set() : new Set(selectable.map((l) => l.key));
    });
  }, [shown]);

  const handleMenu = useMenuNav(toast.push);

  const doClear = useCallback(() => {
    clear();
    setConfirmClear(false);
    setSelected(new Set());
    toast.push('Sepet boşaltıldı');
  }, [clear, toast]);

  const seciliBekleyen = useMemo(
    () => pending.filter((l) => selected.has(l.key)),
    [pending, selected]
  );

  /** Ozet paneli SADECE secili satirlardan hesaplanir —
   *  ekranda gorunen tutar ile siparis edilen kalemler ayni olsun. */
  const seciliTotals = useMemo(() => {
    const units = seciliBekleyen.reduce((s, l) => s + lineUnits(l), 0);
    const gross = seciliBekleyen.reduce((s, l) => s + l.product.grossPrice * lineUnits(l), 0);
    const net = seciliBekleyen.reduce((s, l) => s + l.product.netPrice * lineUnits(l), 0);
    const withTax = withVat(net);
    return {
      gross,
      net,
      discount: gross - net,
      discountPct: gross > 0 ? ((gross - net) / gross) * 100 : 0,
      vat: Math.round((withTax - net) * 100) / 100,
      vatRate: VAT_RATE,
      grand: withTax,
      itemCount: seciliBekleyen.length,
      unitCount: units,
    };
  }, [seciliBekleyen, lineUnits]);

  const doOrder = useCallback(async () => {
    if (gonderKilit.current || gonderiliyor || !seciliBekleyen.length) return;
    gonderKilit.current = true;
    setGonderiliyor(true);
    let sonuc;
    try {
      sonuc = await siparisVer(seciliBekleyen.map((l) => l.key), note);
    } catch (e) {
      sonuc = { ok: false, mesaj: (e && e.message) || 'Sipariş oluşturulamadı' };
    } finally {
      gonderKilit.current = false;
      setGonderiliyor(false);
    }

    if (sonuc.ok) {
      setSelected(new Set());
      setNote('');
      ses.basari();
      setSonSiparis({ kalem: sonuc.kalemSayisi, nolar: sonuc.siparisNolari || [] });
    } else if (sonuc.yetersizStok && sonuc.yetersizStok.length) {
      ses.hata();
      const ilk = sonuc.yetersizStok.slice(0, 3)
        .map((s) => `${s.urunKodu} (${s.depo}: ${s.mevcut} adet)`).join(', ');
      const kalan = sonuc.yetersizStok.length - 3;
      toast.push(`Yetersiz stok: ${ilk}${kalan > 0 ? ` ve ${kalan} ürün daha` : ''}`);
    } else {
      ses.hata();
      toast.push(sonuc.mesaj || 'Sipariş oluşturulamadı');
    }
  }, [gonderiliyor, seciliBekleyen, siparisVer, note, toast]);

  const siparisPenceresiKapat = useCallback(() => setSonSiparis(null), []);

  const deleteSelected = useCallback(() => {
    const keys = [...selected];
    if (!keys.length) return;
    removeMany(keys);
    setSelected(new Set());
    toast.push(`${keys.length} satır kaldırıldı`);
  }, [selected, removeMany, toast]);

  useEffect(() => {
    if (!confirmClear) return undefined;
    const esc = (e) => { if (e.key === 'Escape') setConfirmClear(false); };
    document.addEventListener('keydown', esc);
    const t = setTimeout(() => { if (confirmRef.current) confirmRef.current.focus(); }, 30);
    return () => { document.removeEventListener('keydown', esc); clearTimeout(t); };
  }, [confirmClear]);

  const lockedCount = useMemo(
    () => lines.filter((l) => l.status !== 'pending').length,
    [lines]
  );

  return (
    <div className={styles.page}>
      <ToastStack items={toast.items} onDismiss={toast.dismiss} />
      <Header
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        cart={cart}
        eurRate={EUR_RATE}
        onMenuSelect={handleMenu}
      />

      <main className={styles.main}>
        <div className={page.head}>
          <Link to="/v2/products" className={page.back}>
            <ArrowLeft size={16} /> Alışverişe Devam Et
          </Link>
          <h1 className={page.title}>
            Sepetim
            {!empty && (
              <span className={page.titleCount}>
                {totals.itemCount} ürün · {formatNumber(totals.unitCount)} adet
              </span>
            )}
          </h1>

          {lockedCount > 0 && (
            <div className={page.tabs}>
              <button type="button"
                      className={`${page.tab} ${tab === 'pending' ? page.tabOn : ''}`}
                      onClick={() => setTab('pending')}>
                Bekleyen <span className={page.tabCount}>{pending.length}</span>
              </button>
              <button type="button"
                      className={`${page.tab} ${tab === 'ordered' ? page.tabOn : ''}`}
                      onClick={() => setTab('ordered')}>
                Siparişler <span className={page.tabCount}>{siparisliler.length}</span>
              </button>
              <button type="button"
                      className={`${page.tab} ${tab === 'all' ? page.tabOn : ''}`}
                      onClick={() => setTab('all')}>
                Tümü <span className={page.tabCount}>{lines.length}</span>
              </button>
            </div>
          )}

          {!empty && (
            <button type="button" className={page.clearBtn} onClick={() => setConfirmClear(true)}>
              <Trash2 size={14} /> Sepeti Boşalt
            </button>
          )}
        </div>

        {empty ? (
          <div className={page.empty}>
            <span className={page.emptyIcon}>
              <ShoppingCart size={44} strokeWidth={1.2} />
            </span>
            <h2 className={page.emptyTitle}>Sepetinizde ürün bulunmuyor</h2>
            <p className={page.emptyText}>
              Kataloğumuzdaki binlerce Bosch ürününü inceleyip sepetinize ekleyebilir,
              siparişinizi buradan oluşturabilirsiniz.
            </p>
            <Link to="/v2/products" className={page.emptyBtn}>
              Ürünleri İncele
            </Link>
          </div>
        ) : (
          <div className={page.grid}>
            <div className={page.left}>
              {stockIssues.length > 0 && (
                <div className={page.alert}>
                  <AlertTriangle size={17} />
                  <span>
                    <b>{stockIssues.length} depo satırında</b> istenen adet stoğu aşıyor.
                    Miktarı düşürebilir veya diğer depodan tamamlayabilirsiniz.
                  </span>
                </div>
              )}

              <div className={page.tableCard}>
                {selected.size > 0 && (
                  <div className={page.selBar}>
                    <span className={page.selText}>
                      <CheckSquare size={15} /> <b>{selected.size}</b> satır seçili
                    </span>
                    <button type="button" className={page.selDel} onClick={deleteSelected}>
                      <Trash2 size={14} /> Seçilenleri Sil
                    </button>
                  </div>
                )}

                <CartTable
                  lines={gorunen} selected={selected}
                  onSelect={toggleSelect} onSelectAll={toggleAll}
                  onQty={setQty} onRemove={remove}
                  eurRate={EUR_RATE} lineUnits={lineUnits}
                />

                <div className={page.mobileList}>
                  {gorunen.map((l) => (
                    <MobileCard key={l.key} line={l}
                                onQty={setQty} onRemove={remove} lineUnits={lineUnits} />
                  ))}
                </div>

                {toplamSayfa > 1 && (
                  <div className={page.pager}>
                    <span className={page.pagerInfo}>
                      Toplam {shown.length} satır ·{' '}
                      {(aktifSayfa - 1) * SAYFA_BOYU + 1}-
                      {Math.min(aktifSayfa * SAYFA_BOYU, shown.length)} arası
                    </span>
                    <div className={page.pages}>
                      <button type="button" className={page.pageBtn}
                              onClick={() => setSayfa(aktifSayfa - 1)}
                              disabled={aktifSayfa <= 1} aria-label="Önceki sayfa">
                        <ChevronLeft size={15} />
                      </button>
                      {sayfaListesi.map((n, i) => (n === '…' ? (
                        <span key={`d${i}`} className={page.pageDots}>…</span>
                      ) : (
                        <button key={n} type="button"
                                className={`${page.pageBtn} ${n === aktifSayfa ? page.pageOn : ''}`}
                                onClick={() => setSayfa(n)}>{n}</button>
                      )))}
                      <button type="button" className={page.pageBtn}
                              onClick={() => setSayfa(aktifSayfa + 1)}
                              disabled={aktifSayfa >= toplamSayfa} aria-label="Sonraki sayfa">
                        <ChevronRight size={15} />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className={page.right}>
              <CartSummary
                totals={seciliTotals} eurRate={EUR_RATE}
                note={note} onNoteChange={setNote}
                onCheckout={doOrder}
                loading={gonderiliyor}
                disabled={seciliBekleyen.length === 0 || gonderiliyor}
              />
            </div>
          </div>
        )}
      </main>
      <SiparisGonderimi
        gonderiliyor={gonderiliyor}
        sonuc={sonSiparis}
        onKapat={siparisPenceresiKapat}
      />
      <Footer />

      {confirmClear && (
        <div
          className={page.confirmOverlay}
          onMouseDown={(e) => { if (e.target === e.currentTarget) setConfirmClear(false); }}
          role="dialog" aria-modal="true" aria-label="Sepeti boşalt"
        >
          <div className={page.confirmBox}>
            <span className={page.confirmIcon}><Trash2 size={22} /></span>
            <h2 className={page.confirmTitle}>Sepeti boşaltmak istiyor musunuz?</h2>
            <p className={page.confirmText}>
              Sepetinizdeki <b>{totals.itemCount} ürün</b> ({formatNumber(totals.unitCount)} adet)
              kaldırılacak. Bu işlemi hemen ardından geri alabilirsiniz.
            </p>
            <div className={page.confirmBtns}>
              <button ref={confirmRef} type="button" className={page.confirmCancel}
                      onClick={() => setConfirmClear(false)}>
                Vazgeç
              </button>
              <button type="button" className={page.confirmOk} onClick={doClear}>
                <Trash2 size={15} /> Sepeti Boşalt
              </button>
            </div>
          </div>
        </div>
      )}

      {lastRemoved && (
        <div className={page.undo} role="status">
          <span className={page.undoText}>
            {lastRemoved.lines.length > 1
              ? <><b>{lastRemoved.lines.length} ürün</b> kaldırıldı</>
              : <><b>{lastRemoved.lines[0].product.description}</b> kaldırıldı</>}
          </span>
          <button type="button" className={page.undoBtn} onClick={undoRemove}>
            <Undo2 size={14} /> Geri Al
          </button>
          <button type="button" className={page.undoClose} onClick={dismissUndo}
                  aria-label="Kapat">
            <X size={15} />
          </button>
        </div>
      )}
    </div>
  );
}
